import React, { useEffect, useState } from 'react';
import { Label, NumberInput } from '@/components/ui';
import { SegmentedControl } from '@/components/ui/segmented-control';
import type { MealBreakConfig } from '@/types';
import { AnimatePresence } from 'framer-motion';
import { FeeScheduleInput } from './FeeScheduleInput';
import { CustomizedFieldIndicator } from '../../../../form/CustomizedFieldIndicator';
import { AnimatedFormError } from '../../../../form/AnimatedFormError';
import { fieldLabelClass, fieldDescriptionClass } from '@/components/config_forms/shared/surcharge/fieldStyles';
import { cn } from '@/lib/utils';
import { useMethodSwapAnimation } from '@/components/config_forms/shared/surcharge/useMethodSwapAnimation';
import { SurchargeMethodPanel } from '@/components/config_forms/shared/surcharge/SurchargeMethodPanel';

interface Props {
  safeConfig: MealBreakConfig;
  onMealConfigChange: (updates: Partial<MealBreakConfig>) => void;
  mealConfigCustomized: Partial<Record<keyof MealBreakConfig, boolean>>;
  context: 'user' | 'client';
  fixedFeeAmountProps?: Record<string, unknown>;
  fixedFeeAmountError?: React.ReactNode;
  fixedFeeStructureMode?: 'single' | 'schedule' | null;
  onFixedFeeStructureModeChange?: (mode: 'single' | 'schedule') => void;
}

export const FixedFeeSection: React.FC<Props> = ({
  safeConfig,
  onMealConfigChange,
  mealConfigCustomized,
  context,
  fixedFeeAmountProps,
  fixedFeeAmountError,
  fixedFeeStructureMode,
  onFixedFeeStructureModeChange,
}) => {
  // Handle syncing between single fee and first violation in schedule
  const handleSingleFeeChange = (newFee: number | null) => {
    onMealConfigChange({
      fixedFeeAmount: newFee,
      // Also update the first item in the schedule to keep them in sync
      fixedFeeSchedule: newFee !== null ? [{ fee_amount: newFee }] : null
    });
  };

  const handleScheduleChange = (newSchedule: Array<{ fee_amount: number | null }>) => {
    onMealConfigChange({
      fixedFeeSchedule: newSchedule,
      // Sync the single fee with the first violation
      fixedFeeAmount: newSchedule.length > 0 ? newSchedule[0].fee_amount : null
    });
  };

  const inferredMode: 'single' | 'schedule' =
    (safeConfig.fixedFeeSchedule?.length ?? 0) > 1 ? 'schedule' : 'single';
  const [useSchedule, setUseSchedule] = useState(
    (fixedFeeStructureMode ?? inferredMode) === 'schedule',
  );

  useEffect(() => {
    setUseSchedule((fixedFeeStructureMode ?? inferredMode) === 'schedule');
  }, [fixedFeeStructureMode, inferredMode]);

  // Toggle is UI-only — never mutate the schedule data when switching modes
  const handleModeChange = (schedule: boolean) => {
    setUseSchedule(schedule);
    onFixedFeeStructureModeChange?.(schedule ? 'schedule' : 'single');
  };

  const recurringLayoutKey = safeConfig.recurringMealDeadline ? 'recurring' : 'non-recurring';
  const recurringFeeModeKey = useSchedule ? 'schedule' : 'single';

  const {
    containerStyle: recurringContainerStyle,
    containerRef: recurringContainerRef,
    activePanelRef: recurringPanelRef,
    fadeDuration: recurringFadeDuration,
  } = useMethodSwapAnimation(recurringLayoutKey);

  const {
    containerStyle: detailContainerStyle,
    containerRef: detailContainerRef,
    activePanelRef: detailPanelRef,
    fadeDuration: detailFadeDuration,
  } = useMethodSwapAnimation(recurringFeeModeKey);

  // Recurring mode: toggle between single recurring fee and escalating schedule.
  // We animate non-recurring <-> recurring at the outer layer for consistent
  // crossfades with other method-panel swaps, then animate single <-> schedule
  // inside the recurring layout.
  return (
    <div className="space-y-3">
      <div
        ref={recurringContainerRef}
        className="overflow-hidden"
        style={recurringContainerStyle}
      >
        <div className="relative grid">
          <AnimatePresence mode="sync" initial={false}>
            {!safeConfig.recurringMealDeadline && (
              <SurchargeMethodPanel
                key="non-recurring-layout"
                activePanelRef={recurringPanelRef}
                transitionDuration={recurringFadeDuration}
              >
                <div
                  className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center md:grid-cols-2 md:gap-4"
                  data-anim-audit="fixed-fee-pane"
                  data-anim-audit-pane="non-recurring"
                >
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="fixed-fee-amount" className={fieldLabelClass}>Fixed Fee</Label>
                      <CustomizedFieldIndicator isCustomized={mealConfigCustomized.fixedFeeAmount} context={context} />
                    </div>
                    <p className={cn(fieldDescriptionClass, "hidden md:block")}>
                      A flat dollar amount charged for each missed meal break. The full fee applies regardless of how late the break is.
                    </p>
                  </div>
                  <div className="flex flex-col items-end md:w-full">
                    <div className="max-w-[120px] md:w-full">
                      <NumberInput
                        id="fixed-fee-amount"
                        unit="$"
                        unitPosition="left"
                        step="0.01"
                        min="0.01"
                        placeholder="25.00"
                        value={safeConfig.fixedFeeAmount || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          const numValue = value === '' ? null : parseFloat(value);
                          handleSingleFeeChange(numValue);
                        }}
                        {...(fixedFeeAmountProps || {})}
                      />
                    </div>
                    <AnimatedFormError className="w-full text-left md:text-right mt-1">
                      {fixedFeeAmountError}
                    </AnimatedFormError>
                  </div>
                  <p className={cn(fieldDescriptionClass, "col-span-2 md:hidden")}>
                    A flat dollar amount charged for each missed meal break. The full fee applies regardless of how late the break is.
                  </p>
                </div>
              </SurchargeMethodPanel>
            )}

            {safeConfig.recurringMealDeadline && (
              <SurchargeMethodPanel
                key="recurring-layout"
                activePanelRef={recurringPanelRef}
                transitionDuration={recurringFadeDuration}
              >
                <div className="space-y-3">
                  <div className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center md:grid-cols-2 md:gap-4">
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-1.5">
                        <Label className={fieldLabelClass}>Fee Structure</Label>
                        <CustomizedFieldIndicator isCustomized={mealConfigCustomized.fixedFeeSchedule} context={context} />
                      </div>
                      <p className={cn(fieldDescriptionClass, "hidden md:block")}>
                        Charge the same fee for every missed break, or define an escalating schedule.
                      </p>
                    </div>
                    <div className="flex justify-end">
                      <SegmentedControl
                        ariaLabel="Fee Structure"
                        options={[
                          { value: 'single', label: 'Single Fee' },
                          { value: 'schedule', label: 'Schedule' },
                        ]}
                        value={useSchedule ? 'schedule' : 'single'}
                        onChange={(v) => handleModeChange(v === 'schedule')}
                      />
                    </div>
                    <p className={cn(fieldDescriptionClass, "col-span-2 md:hidden")}>
                      Charge the same fee for every missed break, or define an escalating schedule.
                    </p>
                  </div>

                  {/* Recurring fee details */}
                  <div
                    ref={detailContainerRef}
                    className="overflow-hidden"
                    style={detailContainerStyle}
                    data-anim-audit="fixed-fee-detail-container"
                  >
                    <div className="relative grid">
                      <AnimatePresence mode="sync" initial={false}>
                        {useSchedule && (
                          <SurchargeMethodPanel
                            key="schedule"
                            activePanelRef={detailPanelRef}
                            transitionDuration={detailFadeDuration}
                          >
                            <div
                              className="mt-5 grid grid-cols-1 gap-y-1 md:grid-cols-2 md:gap-4"
                              data-anim-audit="fixed-fee-pane"
                              data-anim-audit-pane="schedule"
                            >
                              <div className="space-y-1 md:self-center">
                                <div className="flex items-center gap-1.5">
                                  <Label className={fieldLabelClass}>Fee Schedule</Label>
                                </div>
                                <p className={cn(fieldDescriptionClass, "mt-1")}>
                                  Define a fee for each missed break. If there are more missed breaks than defined fees, the last fee repeats.
                                </p>
                              </div>
                              <FeeScheduleInput
                                fixedFeeSchedule={safeConfig.fixedFeeSchedule}
                                onFeeScheduleChange={handleScheduleChange}
                              />
                            </div>
                          </SurchargeMethodPanel>
                        )}

                        {!useSchedule && (
                          <SurchargeMethodPanel
                            key="single"
                            activePanelRef={detailPanelRef}
                            transitionDuration={detailFadeDuration}
                          >
                            <div
                              className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center md:grid-cols-2 md:gap-4"
                              data-anim-audit="fixed-fee-pane"
                              data-anim-audit-pane="single"
                            >
                              <div className="min-w-0 space-y-1">
                                <div className="flex items-center gap-1.5">
                                  <Label htmlFor="fixed-fee-recurring" className={fieldLabelClass}>Fee Per Violation</Label>
                                </div>
                                <p className={cn(fieldDescriptionClass, "hidden md:block")}>
                                  The same fee is charged for every missed break, regardless of how many occur or when.
                                </p>
                              </div>
                              <div className="flex flex-col items-end md:w-full">
                                <NumberInput
                                  id="fixed-fee-recurring"
                                  className="w-20"
                                  size="xs"
                                  unit="$"
                                  unitPosition="left"
                                  step="0.01"
                                  min="0.01"
                                  placeholder="0.00"
                                  value={safeConfig.fixedFeeAmount || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    const numValue = value === '' ? null : parseFloat(value);
                                    handleSingleFeeChange(numValue);
                                  }}
                                  {...(fixedFeeAmountProps || {})}
                                />
                                <AnimatedFormError className="w-full text-left md:text-right mt-1">
                                  {fixedFeeAmountError}
                                </AnimatedFormError>
                              </div>
                              <p className={cn(fieldDescriptionClass, "col-span-2 md:hidden")}>
                                The same fee is charged for every missed break, regardless of how many occur or when.
                              </p>
                            </div>
                          </SurchargeMethodPanel>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              </SurchargeMethodPanel>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
